import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { usePublishEquipmentType } from '@/db/usePublishEquipmentType';
import { equipmentTypeDraftStatusState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { hasUnpublishedChanges } from '@rock/dao';
import type { EquipmentTypeId } from '@rock/types';
import type { FC } from 'react';

/**
 * Draft controls for one equipment type. Admins live-preview the draft on-device (preview mode); this
 * just shows whether there are unpublished changes and lets the admin Publish (draft → live) or Discard.
 */
export const EquipmentTypeStagingToolbar: FC<{ typeId: EquipmentTypeId }> = ({ typeId }) => {
    const status = useAsyncAtomValue(equipmentTypeDraftStatusState(typeId), undefined);
    const { busy, publish, discardDraft } = usePublishEquipmentType(typeId);

    // Only show the bar when there's something to publish: a draft that differs from live (or is new).
    if (!hasUnpublishedChanges(status)) return null;

    return (
        <div className="flex flex-row items-center gap-2 mb-4 rounded-md border bg-muted/40 px-3 py-2">
            <Badge variant="outline" className="text-blue-700 border-blue-700 bg-background">
                {status === 'draft-pending' ? 'Unpublished (new)' : 'Unpublished changes'}
            </Badge>
            <div className="flex-1" />
            <Button size="sm" disabled={busy} onClick={publish}>
                Publish
            </Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={discardDraft}>
                Discard
            </Button>
        </div>
    );
};
